import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import {
  buildDataset,
  buildNewsArticle,
} from 'lib/LDJson';
import { shareImage } from 'lib/social';
import { hasFeaturedAuthor, getFeaturedAuthor } from 'lib/featuredAuthor';
import { getCanonical } from 'lib/urlUtils';

import AMPHTMLLink from 'components/AMP/HTMLLink';

export function resolveArticleRobotMetadata(article) {
  const { nativeAd, searchable, sponsoredBy } = article;
  const NBCBrandedSponsorGeneratedContent = 'NBC News Brand Studio';
  const isNBCBrandedSponsorGeneratedContent = sponsoredBy && sponsoredBy.includes(
    NBCBrandedSponsorGeneratedContent,
  );
  const isNotSearchableArticle = !searchable;
  const isNativeAdAndNotSponsoredByNBC = nativeAd && !isNBCBrandedSponsorGeneratedContent;
  const isNativeAdAndSponsoredByNBC = nativeAd && isNBCBrandedSponsorGeneratedContent;

  if (isNotSearchableArticle || isNativeAdAndNotSponsoredByNBC) return <meta name="ROBOTS" content="NOINDEX, NOFOLLOW" />;
  if (isNativeAdAndSponsoredByNBC) return <meta name="ROBOTS" content="INDEX, NOFOLLOW" />;
  return null;
}

function constructMetaData(article, vertical) {
  const {
    description,
    headline,
    primaryImage,
    socialImage,
    taxonomy: {
      topics,
    },
    teaseImage,
    url,
  } = article;

  const image = socialImage || teaseImage || primaryImage || {};
  const imageUrl = (image.url && image.url.primary);

  const getTitle = () => {
    const title = headline.seo || headline.primary;
    return hasFeaturedAuthor(article) ? `${getFeaturedAuthor(article)}: ${title}` : title;
  };
  const resolvedArticleRobotMetadata = resolveArticleRobotMetadata(article);

  const isOpinion = topics.some(({ slug }) => slug.toLowerCase() === 'opinion');

  return (
    <Head>
      <title>{getTitle()}</title>
      <meta name="description" content={description.seo || description.primary} />
      <link rel="canonical" href={getCanonical(url)} />

      <meta property="og:url" content={url.primary} />
      <meta property="og:title" content={headline.social || headline.primary} />
      <meta property="og:description" content={description.primary || description.seo} />
      <meta property="og:image" content={shareImage(imageUrl, vertical, 'og')} />
      <meta property="og:image:secure_url" content={shareImage(imageUrl, vertical, 'og')} />
      <meta property="og:image:type" content={image.encodingFormat || 'image/gif'} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="article" />

      <meta name="twitter:image:src" content={shareImage(imageUrl, vertical, 'twitter')} />
      <meta name="twitter:image" content={shareImage(imageUrl, vertical, 'twitter')} />
      <meta name="twitter:title" content={headline.social || headline.primary} />
      <meta name="twitter:description" content={description.primary || description.seo} />
      <meta name="twitter:card" content="summary_large_image" />

      <meta property="article:opinion" content={isOpinion.toString()} />
      <meta property="article:content_tier" content="free" />

      <meta name="thumbnail" content={shareImage(imageUrl, vertical)} />
      {resolvedArticleRobotMetadata}
    </Head>
  );
}

function ArticleMetadata({ article, vertical }) {
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: `${JSON.stringify(buildDataset(article, vertical))}` }}
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: `${JSON.stringify(buildNewsArticle(article, vertical))}` }}
        />
      </Head>

      {constructMetaData(article, vertical)}

      <AMPHTMLLink content={article} />
    </>
  );
}

ArticleMetadata.propTypes = {
  article: CustomPropTypes.article.isRequired,
  vertical: PropTypes.string.isRequired,
};

export default ArticleMetadata;
